import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, CalendarDays } from 'lucide-react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const pad = (n) => String(n).padStart(2, '0');
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const fmtTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${pad(m || 0)} ${ampm}`;
};

export default function CalendarPage({ user, onLogout }) {
  const today = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(toKey(today));
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/appointments?date=${selected}`)
      .then((res) => {
        if (!alive) return;
        const rows = (res.data || [])
          .filter((a) => a.status !== 'cancelled')
          .sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''));
        setAppointments(rows);
      })
      .catch(() => alive && setAppointments([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [selected]);

  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // Monday-first
    const start = new Date(first);
    start.setDate(first.getDate() - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [month]);

  const monthLabel = month.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  const selectedDate = new Date(`${selected}T00:00:00`);
  const todayKey = toKey(today);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="h-[calc(100vh-4rem)] lg:h-screen flex flex-col lg:flex-row" data-testid="calendar-page">
        {/* Calendar */}
        <section className="flex-1 flex flex-col p-6 lg:p-8 min-h-0">
          <div className="flex items-center justify-between mb-5">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarDays className="w-7 h-7 text-purple-600" />
              <span data-testid="calendar-month-label">{monthLabel}</span>
            </h1>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setMonth(new Date(today.getFullYear(), today.getMonth(), 1)); setSelected(todayKey); }} data-testid="calendar-today-btn">
                Today
              </Button>
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="calendar-prev-btn" aria-label="Previous month">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="calendar-next-btn" aria-label="Next month">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 mb-2">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-xs font-semibold uppercase tracking-wider text-gray-400 py-1">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 grid-rows-6 gap-1.5 flex-1 min-h-0">
            {cells.map((d) => {
              const key = toKey(d);
              const inMonth = d.getMonth() === month.getMonth();
              const isSel = key === selected;
              const isToday = key === todayKey;
              return (
                <button
                  key={key}
                  onClick={() => setSelected(key)}
                  className={[
                    'rounded-xl border text-left p-2 transition-colors flex flex-col',
                    isSel ? 'bg-purple-600 border-purple-600 text-white shadow-md' : 'bg-white border-gray-200 hover:bg-purple-50 hover:border-purple-200',
                    !inMonth && !isSel ? 'text-gray-300 bg-gray-50/60' : '',
                  ].join(' ')}
                  data-testid={`calendar-day-${key}`}
                  aria-pressed={isSel}
                >
                  <span className={[
                    'text-sm font-semibold w-7 h-7 inline-flex items-center justify-center rounded-full',
                    isToday && !isSel ? 'bg-purple-100 text-purple-700' : '',
                  ].join(' ')}>
                    {d.getDate()}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Day list */}
        <aside className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-gray-200 bg-white flex flex-col min-h-0" data-testid="calendar-day-panel">
          <div className="px-6 py-5 border-b border-gray-100">
            <p className="text-xs uppercase tracking-wider text-gray-400">Appointments</p>
            <h2 className="text-lg font-bold text-gray-900" data-testid="calendar-selected-date">
              {selectedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="p-6 text-sm text-gray-500">Loading…</p>
            ) : appointments.length === 0 ? (
              <p className="p-6 text-sm text-gray-500" data-testid="calendar-empty">No appointments</p>
            ) : (
              <ul className="divide-y divide-gray-100" data-testid="calendar-appointment-list">
                {appointments.map((a) => (
                  <li key={a.id} className="px-6 py-3 flex items-center gap-4" data-testid={`calendar-appt-${a.id}`}>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 w-24 shrink-0">
                      <Clock className="w-3.5 h-3.5" />{fmtTime(a.scheduled_time)}
                    </span>
                    <span className="text-sm text-gray-900 truncate">{a.patient_name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </Layout>
  );
}
